import { HarmReport } from '@inplay/models';
import { Dayjs } from 'dayjs';
import { HarmReportAdvancedFilters } from '../pages/harmReports/harmReports.type';
import { AUTO_GENERATED_CREATED_BY } from '../pages/harmReports/harmReportsFilterHelper';
import {
  isAutoGeneratedReport,
  normalizeHarmKeys,
} from '../component/harmReportForm/harmReportFormHelper';

const getHoursRemaining = (expiresAt: Date): number => {
  const now: number = Date.now();
  const expiry: number = new Date(expiresAt).getTime();
  return (expiry - now) / (1000 * 60 * 60);
};

const matchesDeadlineBracket = (
  hoursRemaining: number,
  selectedBracket: string,
): boolean => {
  if (hoursRemaining <= 0) return false;
  if (selectedBracket === '6+') return hoursRemaining > 5;
  const bracketHours: number = Number.parseInt(selectedBracket, 10);
  return hoursRemaining <= bracketHours;
};

const matchesTotalInterventions = (
  actual: number,
  selectedValue: string,
): boolean => {
  if (selectedValue === '9+') return actual >= 9;
  return actual === Number.parseInt(selectedValue, 10);
};

const getTimeMinutes = (date: Date): number => {
  const dateObject: Date = new Date(date);
  return dateObject.getHours() * 60 + dateObject.getMinutes();
};

const dayjsToMinutes = (time: Dayjs): number => {
  return time.hour() * 60 + time.minute();
};

const getFullName = (
  person: { firstName: string; lastName: string } | undefined,
): string => {
  if (!person) return '';
  return `${person.firstName} ${person.lastName}`;
};

const matchesTimeCreatedFrom = (
  report: HarmReport,
  filters: HarmReportAdvancedFilters,
): boolean => {
  if (!filters.timeCreatedFrom) return true;
  const reportMinutes: number = getTimeMinutes(report.createdAt);
  const filterMinutes: number = dayjsToMinutes(filters.timeCreatedFrom);
  return reportMinutes >= filterMinutes;
};

const matchesTimeCreatedUntil = (
  report: HarmReport,
  filters: HarmReportAdvancedFilters,
): boolean => {
  if (!filters.timeCreatedUntil) return true;
  const reportMinutes: number = getTimeMinutes(report.createdAt);
  const filterMinutes: number = dayjsToMinutes(filters.timeCreatedUntil);
  return reportMinutes <= filterMinutes;
};

const matchesPlayerNo = (
  report: HarmReport,
  filters: HarmReportAdvancedFilters,
): boolean => {
  if (!filters.playerNo) return true;
  return report.playerProfile?.id === filters.playerNo;
};

const matchesHarmObserved = (
  report: HarmReport,
  filters: HarmReportAdvancedFilters,
): boolean => {
  if (!filters.harmObserved || filters.harmObserved.length === 0) return true;
  const allSigns: Set<string> = new Set(
    normalizeHarmKeys([
      ...(report.signsOfGamblingHarm?.lengthOfPlay ?? []),
      ...(report.signsOfGamblingHarm?.money ?? []),
      ...(report.signsOfGamblingHarm?.behaviour ?? []),
      ...(report.signsOfGamblingHarm?.other ?? []),
    ]),
  );
  return filters.harmObserved.some((selected: string): boolean =>
    allSigns.has(selected),
  );
};

const matchesHarmInstance = (
  report: HarmReport,
  filters: HarmReportAdvancedFilters,
): boolean => {
  if (!filters.harmInstance) return true;
  return report.harmInstanceCountForPlayer === filters.harmInstance;
};

const matchesInterventionDecision = (
  report: HarmReport,
  filters: HarmReportAdvancedFilters,
): boolean => {
  if (
    !filters.interventionDecision ||
    filters.interventionDecision.length === 0
  )
    return true;
  if (!report.intervention?.decision) return false;
  return filters.interventionDecision.includes(report.intervention.decision);
};

const matchesInterventionStatus = (
  report: HarmReport,
  filters: HarmReportAdvancedFilters,
): boolean => {
  if (!filters.interventionStatus || filters.interventionStatus.length === 0)
    return true;
  if (!report.intervention?.status) return false;
  return filters.interventionStatus.includes(report.intervention.status);
};

const matchesReviewDeadline = (
  report: HarmReport,
  filters: HarmReportAdvancedFilters,
): boolean => {
  if (!filters.reviewDeadline) return true;
  if (!report.expiresAt) return false;
  const hoursRemaining: number = getHoursRemaining(report.expiresAt);
  return matchesDeadlineBracket(hoursRemaining, filters.reviewDeadline);
};

const matchesInterventionDeadline = (
  report: HarmReport,
  filters: HarmReportAdvancedFilters,
): boolean => {
  if (!filters.interventionDeadline) return true;
  if (!report.expiresAt) return false;
  const hoursRemaining: number = getHoursRemaining(report.expiresAt);
  return matchesDeadlineBracket(hoursRemaining, filters.interventionDeadline);
};

const matchesTotalInterventionsOffered = (
  report: HarmReport,
  filters: HarmReportAdvancedFilters,
): boolean => {
  if (!filters.totalInterventionsOffered) return true;
  const actual: number =
    report.playerDetails?.gambling?.interventionsOffered ?? 0;
  return matchesTotalInterventions(actual, filters.totalInterventionsOffered);
};

const matchesTotalInterventionsRequested = (
  report: HarmReport,
  filters: HarmReportAdvancedFilters,
): boolean => {
  if (!filters.totalInterventionsRequested) return true;
  const actual: number =
    report.playerDetails?.gambling?.interventionsRequested ?? 0;
  return matchesTotalInterventions(actual, filters.totalInterventionsRequested);
};

const matchesCreatedBy = (
  report: HarmReport,
  filters: HarmReportAdvancedFilters,
): boolean => {
  if (!filters.createdBy) return true;
  if (filters.createdBy === AUTO_GENERATED_CREATED_BY) {
    return isAutoGeneratedReport(report);
  }
  const reportCreatedBy: string = getFullName(report.createdBy);
  return reportCreatedBy === filters.createdBy;
};

const matchesReviewedBy = (
  report: HarmReport,
  filters: HarmReportAdvancedFilters,
): boolean => {
  if (!filters.reviewedBy) return true;
  const reportReviewedBy: string = getFullName(report.reviewedBy);
  return reportReviewedBy === filters.reviewedBy;
};

const matchesDateReviewedFrom = (
  report: HarmReport,
  filters: HarmReportAdvancedFilters,
): boolean => {
  if (!filters.dateReviewedFrom) return true;
  if (!report.reviewedBy?.signedDate) return false;
  const reviewedDate: Date = new Date(report.reviewedBy.signedDate);
  const fromDate: Date = new Date(filters.dateReviewedFrom);
  fromDate.setHours(0, 0, 0, 0);
  return reviewedDate >= fromDate;
};

const matchesDateReviewedTo = (
  report: HarmReport,
  filters: HarmReportAdvancedFilters,
): boolean => {
  if (!filters.dateReviewedTo) return true;
  if (!report.reviewedBy?.signedDate) return false;
  const reviewedDate: Date = new Date(report.reviewedBy.signedDate);
  const toDate: Date = new Date(filters.dateReviewedTo);
  toDate.setHours(23, 59, 59, 999);
  return reviewedDate <= toDate;
};

const matchesReviewStatus = (
  report: HarmReport,
  filters: HarmReportAdvancedFilters,
): boolean => {
  if (!filters.reviewStatus || filters.reviewStatus.length === 0) return true;
  return filters.reviewStatus.includes(report.reviewStatus);
};

const FilterPredicates = [
  matchesTimeCreatedFrom,
  matchesTimeCreatedUntil,
  matchesPlayerNo,
  matchesHarmObserved,
  matchesHarmInstance,
  matchesInterventionDecision,
  matchesInterventionStatus,
  matchesReviewDeadline,
  matchesInterventionDeadline,
  matchesTotalInterventionsOffered,
  matchesTotalInterventionsRequested,
  matchesCreatedBy,
  matchesReviewedBy,
  matchesDateReviewedFrom,
  matchesDateReviewedTo,
  matchesReviewStatus,
];

export const filterHarmReports = (
  reports: HarmReport[],
  filters: HarmReportAdvancedFilters,
): HarmReport[] => {
  const normalizedFilters: HarmReportAdvancedFilters = filters.harmObserved
    ? { ...filters, harmObserved: normalizeHarmKeys(filters.harmObserved) }
    : filters;
  return reports.filter((report: HarmReport): boolean =>
    FilterPredicates.every((predicate): boolean =>
      predicate(report, normalizedFilters),
    ),
  );
};
